import type { ReactNode } from 'react'
import { fbt, useLocaleContext } from 'fbtee'
import { useOpenEvent } from '../../hooks/useOpenEvent'
import type { EventInterestStatus } from '../../hooks/useUserEventInterests'
import type { FriendEventInterestBuckets } from '../../hooks/useFriendEventInterests'
import { todayLocalISO } from '../../lib/formatDate'
import { Section } from '../../components/Section'
import { EventCard } from '../../components/EventCard'
import { NextTogetherCard } from './NextTogetherCard'
import { LinkedEventCard, UnlinkMenuItem } from './LinkedEventCard'
import { LinkEventEmptyCard } from './LinkEventEmptyCard'
import { MembersSection } from './MembersSection'
import type { GroupView } from './GroupViewNav'
import { PreviousEventsDisclosure } from './PreviousEventsDisclosure'
import type { LinkedEvent } from './splitLinkedEvents'
import type { Group, UserProfile } from '../../types'

interface Props {
  view: Exclude<GroupView, 'totems'>
  group: Group
  upcoming: LinkedEvent[]
  past: LinkedEvent[]
  eventsLoading: boolean
  memberProfiles: Record<string, UserProfile>
  profilesLoading: boolean
  memberInterests: Map<string, FriendEventInterestBuckets>
  eventStatuses: Map<string, EventInterestStatus>
  userId: string | null
  /** Members can unlink events, link new ones and manage totems; guests only RSVP. */
  isMember: boolean
  onSetEventInterest?: (eventId: string, status: EventInterestStatus) => void
  onUnlinkEvent: (groupEventId: string) => void
  /** Whether the viewer may unlink this link (see canUnlinkGroupEvent). */
  canUnlink: (ge: LinkedEvent['ge']) => boolean
  /** The suggested-events section, shown above the upcoming list. */
  pendingSection?: ReactNode
  onSetAdmin?: (uid: string, makeAdmin: boolean) => void
  onRemoveMember?: (uid: string) => void
}

/** The group page body, one scroll: next together → upcoming → members → totems → previous. */
export function GroupPlanSections({
  view, group, upcoming, past, eventsLoading, memberProfiles, profilesLoading,
  memberInterests, eventStatuses, userId, isMember, onSetEventInterest, onUnlinkEvent,
  canUnlink, pendingSection, onSetAdmin, onRemoveMember,
}: Props) {
  const openEvent = useOpenEvent()
  const { locale } = useLocaleContext()
  const today = todayLocalISO()
  const statusOf = (eventId: string) => eventStatuses.get(eventId) ?? 'none'
  const setStatusFor = (eventId: string) =>
    onSetEventInterest ? (next: EventInterestStatus) => onSetEventInterest(eventId, next) : undefined

  function renderCard({ ge, event }: LinkedEvent) {
    const status = statusOf(event.id)
    return (
      <LinkedEventCard
        key={ge.id}
        event={event}
        buckets={memberInterests.get(event.id)}
        profiles={memberProfiles}
        locale={locale}
        status={status}
        onOpen={() => openEvent(event, status)}
        onSetStatus={setStatusFor(event.id)}
        onUnlink={isMember ? () => onUnlinkEvent(ge.id) : undefined}
        unlinkDisabled={!canUnlink(ge)}
      />
    )
  }

  // Suggestions take the hero's place at the top; the next event then sits in the list with the rest.
  const next = pendingSection ? undefined : upcoming[0]
  const laterUpcoming = pendingSection ? upcoming : upcoming.slice(1)
  const nextStatus = next ? statusOf(next.event.id) : undefined
  const hasEvents = upcoming.length > 0 || past.length > 0

  if (view === 'members') {
    return (
      <MembersSection
        group={group}
        memberProfiles={memberProfiles}
        profilesLoading={profilesLoading}
        onSetAdmin={onSetAdmin}
        onRemoveMember={onRemoveMember}
      />
    )
  }

  return (
    <>
      {pendingSection}
      {eventsLoading ? (
        <Section loading>
          <EventCard loading />
        </Section>
      ) : next ? (
        <NextTogetherCard
          event={next.event}
          status={nextStatus ?? 'none'}
          buckets={memberInterests.get(next.event.id)}
          profiles={memberProfiles}
          currentUserId={userId}
          today={today}
          locale={locale}
          onOpen={() => openEvent(next.event, nextStatus ?? 'none')}
          onSetStatus={setStatusFor(next.event.id)}
          menuFooter={isMember ? <UnlinkMenuItem onUnlink={() => onUnlinkEvent(next.ge.id)} disabled={!canUnlink(next.ge)} /> : undefined}
        />
      ) : (
        !hasEvents && !pendingSection && <LinkEventEmptyCard canLink={isMember} />
      )}

      {laterUpcoming.length > 0 && (
        <Section title={fbt('Upcoming', 'Section heading for upcoming events linked to the group')}>
          {laterUpcoming.map(renderCard)}
        </Section>
      )}

      {past.length > 0 && (
        <PreviousEventsDisclosure count={past.length}>{past.map(renderCard)}</PreviousEventsDisclosure>
      )}
    </>
  )
}
